/**
 * Curated “What’s new” notes for returning players.
 *
 * When you ship player-facing changes:
 * 1. Prepend a new entry with a unique `id` (date + short slug).
 * 2. Set `commit` to the short (or full) git SHA this note ships with — internal only.
 * 3. Keep bullets short (3–6). Oldest entries can be trimmed later.
 *
 * Storage (`sots-whats-new-v1`): last seen entry id + that entry’s commit (and when
 * they dismissed). Brand-new devices seed silently; returning players see unread notes.
 * Commit is never shown in the UI — support / debugging only.
 */

export type WhatsNewEntry = {
  /** Stable unique id — never reuse. Newest entry is WHATS_NEW[0]. */
  id: string;
  /**
   * Git commit this note is associated with (short or full SHA).
   * Internal bookkeeping only — not shown to players.
   */
  commit?: string;
  title: string;
  /** Optional short date line for the dialog */
  date?: string;
  bullets: string[];
};

/**
 * Newest first. Only the entries after the player’s last-seen id are shown.
 */
export const WHATS_NEW: readonly WhatsNewEntry[] = [
  {
    id: "2026-08-13-settled-wisdom",
    commit: "local",
    title: "Settled · Wisdom",
    date: "August 2026",
    bullets: [
      "New lie: Settled — a closed teaching that peels by color. Matching Word peels a shell; off-type only chips it as a III",
      "It owns its column: slams Mind on arrival, walks over whatever is ahead, and leans the layer hard toward Lie",
      "Wisdom verses with art and voice: James 1:5, James 3:16–17, 1 Cor 14:33, Col 2:8",
      "Protocol: Settled. No Wisdom owned? James 1:5 is gifted. Coach shows which Answer colors your deck is missing",
      "Col 2:8 lays aside a brick; James 3 / 1 Cor 14:33 wipe every Confusion",
      "Saved loadouts stay as you left them. Change that deck’s cards and its checkpoint is retired — resume is that run, not your new list",
    ],
  },
  {
    id: "2026-08-13-clearer-cards",
    /** Ship commit for this note — update when the note lands on master. */
    commit: "47dbeec",
    title: "Clearer cards",
    date: "August 2026",
    bullets: [
      "Card faces lead with the verse — book name and a large chapter:verse on the art",
      "Smoother drag from the hand; the reference stays readable while you aim",
      "Contest only the next column; a packed row frays everyone standing on it",
      "Free play hub: tap a verse to study it; Resume or New run when a checkpoint is ready",
    ],
  },

  {
    id: "2026-08-12-empty-words",
    commit: "696e403",
    title: "Empty Words",
    date: "August 2026",
    bullets: [
      "New lie: Empty Words — “Your prayers don't count.” It hunts keeps instead of dying in leftover gaps",
      "Free play and Muster: Protocol: Empty Words, plus a late-open teaching spawn",
      "When most slots are Prayers, every protocol hammers the wall instead of spawn-and-die",
      "Counter: mend worn keeps (1 Thess 5:17) and Answer the accusation — Faith still meets it",
    ],
  },

  {
    id: "2026-08-12-gideon-doubt",
    /** Ship commit for this note — update when the note lands on master. */
    commit: "42af1f1",
    title: "The Three Hundred · Doubt",
    date: "August 2026",
    bullets: [
      "New campaign: The Three Hundred (Judges 6–7) — winepress to three hundred, full story art and lie voices",
      "Doubt: convince with Faith, not chip-kill — peel outer shells, then settle the core (or Redeem when exposed)",
      "Gather (Eccl 4:9–10): pull residual Prayers into a keep — unique finale reward for Gideon, with art and voice",
      "Gallery by chapter with featured covers; main menu refreshed for campaign pick",
      "End of fight: moments, flavor line, and clearer stats (including endless best)",
      "Campaign Easy / Hard pressure retuned so sparse scripts still feel like a fight",
    ],
  },

  {
    id: "2026-08-07-deck-copies-changelog",
    /** Ship commit for this note — update when the note lands on master. */
    commit: "7a6ca3f",
    title: "Deck copies & update history",
    date: "August 2026",
    bullets: [
      "Deck builder shows how many copies you own of each verse (×N badge)",
      "Add more than one of the same verse to a loadout — up to what you own",
      "What’s new popup: one note at a time, with Next to cycle and Close to dismiss",
      "Settings → What’s new: re-read past notes anytime (newest 3; expand for older)",
    ],
  },

  {
    id: "2026-08-07-battle-board-feedback",
    /** Ship commit for this note — update when the note lands on master. */
    commit: "174a13b",
    title: "Clearer battle board",
    date: "August 2026",
    bullets: [
      "Lies and Path prayers show arrows toward the slot they are about to enter",
      "Multiplying lies show fork lines to empty ground (and a brief trail when a copy lands)",
      "Answers, Redeem, silence, and Still flash on the board — less popup spam mid-fight",
      "Contesting units get a faint grind link so you can see who is wearing whom",
      "Prayers land in the empty slot you aim at (layer-prefer no longer snaps to center)",
      "Settings → Motion (Full / Reduced / Off) and Text (Off by default; Minimal / Full optional)",
    ],
  },
  {
    id: "2026-08-07-hand-touch-cards",
    /** Ship commit for this note — update when the note lands on master. */
    commit: "a7d437e",
    title: "Clearer hand on phone",
    date: "August 2026",
    bullets: [
      "Small screens: hand cards fan out so verse references stay readable",
      "More reliable touch drag — less fighting with in-app browsers",
      "Coach tips stay visible instead of hiding under the board",
      "Verse card effects refreshed (clearer wording; some gain a brief Still)",
      "Proverbs card is now Prov 3:6 (updated art and voice)",
    ],
  },
  {
    id: "2026-04-06-teach-and-target",
    /** Ship commit for this note — update when the note lands on master. */
    commit: "902581a",
    title: "Easier first fights",
    date: "April 2026",
    bullets: [
      "Staged tips: how to drag, speech lock, plant a Prayer, and clear the Will",
      "Coach shows Answer and Abide cards from this fight’s deck",
      "Valid drop slots light up more clearly; invalid ones dim",
      "Pause → How to fight anytime · Settings can replay tutorial tips",
    ],
  },
] as const;

const STORAGE_KEY = "sots-whats-new-v1";

/** What we persist after the player acknowledges (or is seeded to) a note. */
export type WhatsNewSeenState = {
  lastId: string;
  /** Commit recorded from the entry (if authored). */
  lastCommit?: string;
  /** ISO timestamp when we wrote this state. */
  seenAt?: string;
};

export function loadWhatsNewSeen(): WhatsNewSeenState | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v || !v.trim()) return null;
    const raw = v.trim();
    // Legacy: plain id string
    if (raw[0] !== "{") {
      return { lastId: raw };
    }
    const parsed = JSON.parse(raw) as Partial<WhatsNewSeenState>;
    if (!parsed.lastId || typeof parsed.lastId !== "string") return null;
    return {
      lastId: parsed.lastId,
      lastCommit:
        typeof parsed.lastCommit === "string" ? parsed.lastCommit : undefined,
      seenAt: typeof parsed.seenAt === "string" ? parsed.seenAt : undefined,
    };
  } catch {
    return null;
  }
}

export function loadWhatsNewSeenId(): string | null {
  return loadWhatsNewSeen()?.lastId ?? null;
}

export function saveWhatsNewSeen(state: WhatsNewSeenState): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        lastId: state.lastId,
        ...(state.lastCommit ? { lastCommit: state.lastCommit } : {}),
        seenAt: state.seenAt ?? new Date().toISOString(),
      }),
    );
  } catch {
    /* ignore */
  }
}

/** @deprecated Prefer saveWhatsNewSeen — kept for call-site clarity. */
export function saveWhatsNewSeenId(id: string): void {
  const entry = WHATS_NEW.find((e) => e.id === id);
  saveWhatsNewSeen({
    lastId: id,
    lastCommit: entry?.commit,
  });
}

/** Latest changelog id (or null if none authored). */
export function latestWhatsNewId(): string | null {
  return WHATS_NEW[0]?.id ?? null;
}

export function latestWhatsNewEntry(): WhatsNewEntry | null {
  return WHATS_NEW[0] ?? null;
}

/**
 * Entries the player has not acknowledged, newest first.
 * - No last-seen + no progress → seed silently later (return []).
 * - No last-seen + progress → treat as returning; show newest only.
 * - Last-seen found → every entry newer than that id.
 */
export function unseenWhatsNew(opts: {
  hasProgress: boolean;
}): WhatsNewEntry[] {
  if (!WHATS_NEW.length) return [];
  const last = loadWhatsNewSeenId();
  if (!last) {
    if (!opts.hasProgress) return [];
    return [WHATS_NEW[0]!];
  }
  const idx = WHATS_NEW.findIndex((e) => e.id === last);
  if (idx === 0) return []; // already current
  if (idx < 0) {
    // Unknown / purged id — show only the latest note once
    return [WHATS_NEW[0]!];
  }
  return WHATS_NEW.slice(0, idx);
}

/** Mark the catalog as fully caught up (call after dismiss). */
export function markWhatsNewCaughtUp(): void {
  const latest = latestWhatsNewEntry();
  if (!latest) return;
  saveWhatsNewSeen({
    lastId: latest.id,
    lastCommit: latest.commit,
  });
}

/**
 * First visit with no progress: remember current latest without a popup
 * so only *future* updates interrupt them.
 */
export function seedWhatsNewIfNeeded(opts: { hasProgress: boolean }): void {
  if (loadWhatsNewSeenId()) return;
  if (opts.hasProgress) return; // returning path will show unread
  const latest = latestWhatsNewEntry();
  if (!latest) return;
  saveWhatsNewSeen({
    lastId: latest.id,
    lastCommit: latest.commit,
  });
}

export { STORAGE_KEY as WHATS_NEW_STORAGE_KEY };
